<template>
    <h1 class="page-title">Gérer les transporteurs</h1>
    <div class="flex justify-end ml-auto">

        <Button
            label="Ajouter un transporteur"
            @click="setUuidAndOpenModal('')"
            icon="pi pi-plus"
            aria-label="Ajouter un transporteur"
            class="button-add mb-2"
        />
    </div>
    <div class="card">
        <DataTable
          lazy
          :value="tableData"
          :paginator="true"
          :rows="rows"
          removableSort
          :loading="isLoading"
          :totalRecords="totalRecords"
          :currentPage="currentPage"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 25, 100]"
          currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
          @page="onPaginate($event)"
          @sort="onSort($event)"
        >
      <template #header>
          <div class="flex flex-row flex-nowrap card-header border-0">                 
                <div class="p-input-icon-left">
                    <i class="pi pi-search" />
                    <InputText type="text" v-model="criteria"
                               placeholder="Recherche par transporteur"  @keyup.enter="onFilterDt" @input="onClearInputFilter" />
                </div>
                
              
          </div>
          <div class="flex  justify-end w-full mt-4">
              Nombre total de transporteurs : {{ totalRecords }}
          </div>
      </template>
      <div>
        <Column field="name" header="Nom du transporteur">
          <template #body="slotProps">
            <span> {{ slotProps.data.name }} </span>
          </template>
        </Column>
        <Column field="isActive" header="Etat" sortable>
          <template #body="slotProps">
            <span v-if="slotProps.data.isActive" class="badge badge-success">
              Actif
            </span>
            <span v-else class="badge badge-danger"> Inactif </span>
          </template>
        </Column>
        <Column field="createdAt" header="Date de création" sortable>
          <template #body="slotProps">
            <span> {{ slotProps.data.createdAt }} </span>
          </template>
        </Column>
        <Column field="action" header="Action">
          <template #body="slotProps">
            <div class="d-flex flex-row">
              <Button size="small" icon="pi pi-pencil" class="p-edition" 
                  style="margin-right: 0.5em" title="Éditer" @click="setUuidAndOpenModal(slotProps.data.uuid)">
              </Button>

              <Button size="small" icon="pi pi-trash" class="p-button-danger" 
                  style="margin-right: 0.5em" title="Éditer" @click="onDelete(slotProps.data)">
              </Button>
              <InputSwitch
                v-model="slotProps.data.isActive"
                :input-id="slotProps.data.uuid"
                @input="changeState(slotProps.data.uuid, slotProps.data.isActive)"
              />
            </div>
          </template>
        </Column>
      </div>
      <template #empty>Aucun transporteur ne correspond à votre recherche</template>
    </DataTable>
    <TransporterFormModal
      :transporter-uuid="transporterUuid"
      :transporter-form-modal-id="transporterFormModalId"
      @transporter-created="relaodPage"
      @transporter-updated="relaodPage"
    />
  </div>
</template>

<script setup lang="ts">
import DataTable from "primevue/datatable";
import { onMounted, ref } from "vue";
import { ITransporterList } from "@/models/transporter/ITransporterList";
import {
  fetchTransporter,
  triggerTransporterState,
  deleteTransporter,
} from "@/_services/transporter/transporter";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Column from "primevue/column";
import InputSwitch from "primevue/inputswitch";
import swal from "@/_helpers/swal";
import TransporterFormModal from "@/components/modal/transporter/TransporterFormModal.vue";
import router from "@/router";

const transporterFormModalId = "supplierFormModal";
const transporter = Array<ITransporterList>();
const tableData = ref<Array<ITransporterList>>(transporter);
const rows = ref<number>(10);
const isLoading = ref<boolean>(true);
const totalRecords = ref<number>(0);
const currentPage = ref<number>(0);
const criteria = ref<string>("");
const page = ref<number>(1);
const modeSortable = ref<boolean>(false);
const defaultsSort = {
  fieldLabel: "order[createdAt]",
  fieldValue: "desc",
};
const transporterUuid = ref<string>("");

onMounted(async () => {
  await populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
});

const populateDatatable = async (itemsPerPage, page, criteria?, sort?) => {
  const [loadingDt, totalRecordsDt, tableDataDt] = await fetchTransporter(
    itemsPerPage,
    page,
    criteria,
    sort
  );
  totalRecords.value = totalRecordsDt.value;
  tableData.value = tableDataDt.value;
  isLoading.value = loadingDt.value;
};

async function onPaginate(e) {
  rows.value = e.rows;
  populateDatatable(e.rows, e.page + 1, criteria.value, defaultsSort);
}

const onSort = (e) => {
  if (e.sortField) {
    const sort = {
      fieldLabel: "order[" + e.sortField + "]",
      fieldValue: e.sortOrder === 1 ? "asc" : "desc",
    };
    modeSortable.value = true;
    populateDatatable(rows.value, page.value, criteria.value, sort);
  } else {
    modeSortable.value = false;
    populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
  }
};

const onFilterDt = () => {
    if (criteria.value.trim() != "") {
    populateDatatable(rows.value, page.value, criteria.value.trim(), defaultsSort);
  }
};

const onClearInputFilter = () => {
  if (criteria.value.trim() == "") {
    populateDatatable(rows.value, page.value,"", defaultsSort);
  }
};

const onDelete = (data: ITransporterList) => {
  swal
    .confirm("Êtes vous sur de vouloir archiver le transporteur ?")
    .then((result) => {
      if (result.isConfirmed) {
        deleteTransporter(data.uuid).then(() => {
          swal.success("Archivé !", "Vous venez d'archiver un transporteur");
          refreshDatatable();
        });
      }
    });
};

const changeState = async (uuid: string, isActive: boolean) => {
  let currentStateBeforeSave = !isActive;
  const confirmationMessage = currentStateBeforeSave
    ? "Êtes-vous sûr de vouloir désactiver le transporteur ?"
    : "Êtes-vous sûr de vouloir activer le transporteur ?";

  const result = await swal.confirm(confirmationMessage);

  if (result.isConfirmed) {
    await triggerTransporterState(uuid, isActive);
    const successMessage = isActive
      ? "Vous venez d'activer un transporteur."
      : "Vous venez de désactiver un transporteur.";

    swal.success("Opération réussie !", successMessage);
    refreshDatatable();
    //relaodPage();
  }
};
const setUuidAndOpenModal = (uuid: string) => {
  transporterUuid.value = uuid;
  const transporterFormModalElement:HTMLDialogElement | null = document.querySelector(`#${transporterFormModalId}`);
  if (transporterFormModalElement) {
    transporterFormModalElement.showModal();
  }
};

const refreshDatatable = () => {
  console.dir("IS EMIT")
  populateDatatable(rows.value, page.value, criteria.value, defaultsSort);
};

const relaodPage = () => {
    window.location.reload();
};

</script>

